import { Alert, AlertDescription } from '@startupquickstart/frontend';
import { AlertTriangle } from 'lucide-react';

interface Voiceover {
  id: string;
  fileName: string;
  name: string;
  fileType: string;
  duration?: number;
  url?: string;
  generationProvider?: string | null;
  generationModel?: string | null;
  generationVoice?: string | null;
}

interface VoiceoverAudioPlayerProps {
  /**
   * Voiceover object with audio file details
   */
  voiceover: Voiceover | null | undefined;

  /**
   * Sync status from API
   * - true: In sync
   * - false: Out of sync (show warning)
   * - null: Unable to determine (don't show warning)
   */
  isInSync: boolean | null;

  /**
   * Message to display in warning alert when out of sync
   */
  outOfSyncMessage?: string;
}

/**
 * Consistent voiceover audio player with sync status warning
 * Used across tours, attractions, translations, and themes
 */
export function VoiceoverAudioPlayer({
  voiceover,
  isInSync,
  outOfSyncMessage = 'Content has changed since voiceover was generated',
}: VoiceoverAudioPlayerProps) {
  if (!voiceover) return <span className="text-muted-foreground">-</span>;

  return (
    <div className="space-y-2">
      {/* Audio Player */}
      <audio controls className="w-full max-w-md">
        <source src={voiceover.url} type={voiceover.fileType} />
        Your browser does not support the audio element.
      </audio>

      {/* Generation Metadata */}
      {voiceover.generationProvider && (
        <p className="text-xs text-muted-foreground">
          {voiceover.generationProvider} {voiceover.generationModel}
          {voiceover.generationVoice ? ` / ${voiceover.generationVoice}` : ''}
        </p>
      )}

      {/* Out of Sync Warning - Only show if explicitly false */}
      {isInSync === false && (
        <Alert variant="destructive" className="max-w-md">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{outOfSyncMessage}</AlertDescription>
        </Alert>
      )}
    </div>
  );
}
